import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFurnishImageHistory } from "../api/businesses";
import { saveAs } from "file-saver";

interface Variation {
  order: number;
  url: string;
  createdAt: string;
}

const VariationsViewer: React.FC = () => {
  const { imageId } = useParams<{ imageId: string }>();
  const navigate = useNavigate();
  const [variations, setVariations] = useState<Variation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchVariations = async () => {
      try {
        if (!imageId) {
          navigate("/history");
          return;
        }

        const response = await getFurnishImageHistory();
        const images = response.data.data;

        const image = images.find((img: any) => img.id === imageId);

        if (image) {
          const variationsData = image.variations || [
            {
              order: 1,
              url: image.afterUrl,
              createdAt: image.updatedAt || image.createdAt,
            },
          ];
          setVariations(variationsData);
          setSelectedImage(
            variationsData[variationsData.length - 1]?.url || null
          );
        } else {
          alert("Image not found.");
          navigate("/history");
        }
      } catch (error) {
        console.error("Error fetching variations:", error);
        alert("Failed to load variations.");
        navigate("/history");
      } finally {
        setLoading(false);
      }
    };

    fetchVariations();
  }, [imageId, navigate]);

  const handleDownload = (url: string, order: number) => {
    saveAs(url, `variation-${order}.jpg`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex-1"></div>
          <h2 className="text-xl md:text-3xl font-bold flex-1 text-center">
            Image Variations
          </h2>
          <div className="flex-1 flex justify-end">
            <button
              onClick={() => navigate("/history")}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Back to History
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Main Image Display */}
        <div className="lg:col-span-2 flex flex-col h-[644px]">
          <div className="bg-white rounded-lg shadow-lg p-4 flex-1 flex items-center justify-center">
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Selected variation"
                className="max-w-full max-h-full object-contain rounded"
              />
            )}
          </div>
        </div>

        {/* Variations List */}
        <div className="flex flex-col h-[644px]">
          <h3 className="text-lg font-semibold mb-4">
            All Variations ({variations.length})
          </h3>
          <div className="flex-1 overflow-y-auto pr-2 space-y-3">
            {variations.map((variation) => (
              <div
                key={variation.order}
                className={`bg-white rounded-lg shadow p-3 cursor-pointer transition-all ${
                  selectedImage === variation.url
                    ? "ring-2 ring-blue-500 ring-inset"
                    : "hover:shadow-md"
                }`}
                onClick={() => setSelectedImage(variation.url)}
              >
                <div className="flex items-center space-x-3">
                  <img
                    src={variation.url}
                    alt={`Variation ${variation.order}`}
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="flex-grow">
                    <p className="font-semibold text-sm">
                      Variation {variation.order}
                    </p>
                    <p className="text-xs text-gray-600">
                      {new Date(variation.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(variation.url, variation.order);
                    }}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                  >
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariationsViewer;
